import React, { useEffect, useState } from 'react';

/**
 * UndoToast — single horizontal stripe used for the receptionist's
 * "Cancelling X · Undo · Ns" affordance.
 *
 * Pure presentation. Caller owns the timer (see useDeferredAction).
 *
 * Props:
 *   label      — short verb-phrase, e.g. "Cancelling Filling 16"
 *   expiresAt  — Date.now() ms when the action fires
 *   onUndo()   — fires when the user clicks Undo
 */
export default function UndoToast({ label, expiresAt, onUndo }) {
  // Re-render every 250ms so the countdown reads in real time. We don't
  // need a more accurate clock than that for a 5s toast.
  const [, force] = useState(0);
  useEffect(() => {
    const t = setInterval(() => force((n) => n + 1), 250);
    return () => clearInterval(t);
  }, []);

  const remainingMs = Math.max(0, expiresAt - Date.now());
  const remainingSec = Math.ceil(remainingMs / 1000);

  return (
    <div
      role="alert"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '0.5rem 0.625rem',
        borderRadius: 8,
        background: '#1e293b',
        color: '#f8fafc',
        fontSize: '0.75rem',
        boxShadow: '0 6px 20px rgba(15,23,42,0.25)',
      }}
    >
      <span aria-hidden="true">⏳</span>
      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}
      </span>
      <span style={{ fontVariantNumeric: 'tabular-nums', opacity: 0.85 }}>
        {remainingSec}s
      </span>
      <button
        type="button"
        onClick={onUndo}
        className="px-2 py-0.5 rounded-md bg-white text-slate-900 text-xs font-semibold
                   hover:bg-slate-100"
      >
        Undo
      </button>
    </div>
  );
}
